import { useState, useEffect } from "react";
import { authAxios } from "../../utils/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  RefreshCw, 
  FileText, 
  User, 
  AlertTriangle,
  CheckCircle,
  Trash2,
  Eye,
  ChevronDown,
  ChevronUp,
  Blocks,
  Copy,
  Database,
  File
} from "lucide-react";

export default function V3Artifacts() {
  const [artifacts, setArtifacts] = useState({ ref_sheets: [], style_cards: [] });
  const [staleCounts, setStaleCounts] = useState({ stale_ref_sheets: 0, stale_style_cards: 0 });
  const [coreBlocks, setCoreBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState({});
  const [expandedArtifact, setExpandedArtifact] = useState(null);
  const [expandedBlock, setExpandedBlock] = useState(null);
  const [artifactDetails, setArtifactDetails] = useState({});
  const [blockDetails, setBlockDetails] = useState({});
  const [combinedRules, setCombinedRules] = useState(null);
  const [showCombinedRules, setShowCombinedRules] = useState(false);

  const fetchArtifacts = async () => {
    try {
      setLoading(true);
      const [artifactsRes, staleRes, blocksRes] = await Promise.all([
        authAxios.get("/v3/admin/artifacts/list"),
        authAxios.get("/v3/admin/stale-count"),
        authAxios.get("/v3/admin/core-blocks")
      ]);
      setArtifacts(artifactsRes.data);
      setStaleCounts(staleRes.data);
      setCoreBlocks(blocksRes.data.blocks || []);
    } catch (error) {
      console.error("Failed to fetch artifacts:", error);
      toast.error("Failed to load V3 artifacts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArtifacts();
  }, []);

  const regenerateRefSheet = async (entityType, entityId) => {
    const key = `ref_${entityId}`;
    setRegenerating(prev => ({ ...prev, [key]: true }));
    try {
      await authAxios.post("/v3/ref-sheet/generate", {
        entity_type: entityType,
        entity_id: entityId,
        force: true
      });
      toast.success("Ref sheet regenerated");
      fetchArtifacts();
    } catch (error) {
      toast.error("Failed to regenerate ref sheet");
    } finally {
      setRegenerating(prev => ({ ...prev, [key]: false }));
    }
  };

  const regenerateStyleCard = async (matchId) => {
    const key = `style_${matchId}`;
    setRegenerating(prev => ({ ...prev, [key]: true }));
    try {
      await authAxios.post("/v3/style-card/generate", {
        match_id: matchId,
        force: true
      });
      toast.success("Style card regenerated");
      fetchArtifacts();
    } catch (error) {
      toast.error("Failed to regenerate style card");
    } finally {
      setRegenerating(prev => ({ ...prev, [key]: false }));
    }
  };

  const deleteArtifacts = async (entityType, entityId) => {
    if (!confirm(`Delete all artifacts for ${entityId}?`)) return;
    try {
      await authAxios.delete(`/v3/admin/artifacts/${entityType}/${entityId}`);
      toast.success("Artifacts deleted");
      fetchArtifacts();
    } catch (error) {
      toast.error("Failed to delete artifacts");
    }
  };

  const viewRefSheet = async (entityType, entityId) => {
    const key = `ref_${entityId}`;
    if (expandedArtifact === key) {
      setExpandedArtifact(null);
      return;
    }
    try {
      const res = await authAxios.get(`/v3/ref-sheet/${entityType}/${entityId}`);
      setArtifactDetails(prev => ({ ...prev, [key]: res.data }));
      setExpandedArtifact(key);
    } catch (error) {
      toast.error("Failed to load ref sheet details");
    }
  };

  const viewStyleCard = async (matchId) => {
    const key = `style_${matchId}`;
    if (expandedArtifact === key) {
      setExpandedArtifact(null);
      return;
    }
    try {
      const res = await authAxios.get(`/v3/style-card/${matchId}`);
      setArtifactDetails(prev => ({ ...prev, [key]: res.data }));
      setExpandedArtifact(key);
    } catch (error) {
      toast.error("Failed to load style card details");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleString();
  };

  const viewBlockContent = async (siloId) => {
    if (expandedBlock === siloId) {
      setExpandedBlock(null);
      return;
    }
    try {
      const res = await authAxios.get(`/v3/admin/core-blocks/${siloId}`);
      setBlockDetails(prev => ({ ...prev, [siloId]: res.data }));
      setExpandedBlock(siloId);
    } catch (error) {
      toast.error("Failed to load block content");
    }
  };

  const viewCombinedRules = async () => {
    if (showCombinedRules) {
      setShowCombinedRules(false);
      return;
    }
    try {
      const res = await authAxios.get("/v3/admin/core-blocks/combined/coaching-global-rules");
      setCombinedRules(res.data);
      setShowCombinedRules(true);
    } catch (error) {
      toast.error("Failed to load combined rules");
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-divine-gold" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-divine-charcoal border-divine-gold/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Core Blocks</p>
                <p className="text-2xl font-bold text-divine-gold">{coreBlocks.length}</p>
              </div>
              <Blocks className="h-8 w-8 text-divine-gold/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-divine-charcoal border-divine-gold/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Ref Sheets</p>
                <p className="text-2xl font-bold text-divine-gold">{artifacts.counts?.ref_sheets || 0}</p>
              </div>
              <FileText className="h-8 w-8 text-divine-gold/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-divine-charcoal border-divine-gold/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Style Cards</p>
                <p className="text-2xl font-bold text-divine-gold">{artifacts.counts?.style_cards || 0}</p>
              </div>
              <User className="h-8 w-8 text-divine-gold/50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-divine-charcoal border-divine-gold/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-400">Stale Artifacts</p>
                <p className="text-2xl font-bold text-yellow-500">{staleCounts.stale_ref_sheets + staleCounts.stale_style_cards}</p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-500/50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Core Blocks Panel */}
      <Card className="bg-divine-charcoal border-divine-gold/20">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-divine-gold flex items-center gap-2">
              <Blocks className="h-5 w-5" />
              Core Blocks (Coaching Global Rules)
            </CardTitle>
            <Button
              size="sm"
              variant="outline"
              onClick={viewCombinedRules}
              className="border-divine-gold/50 text-divine-gold hover:bg-divine-gold/10"
              data-testid="view-combined-rules"
            >
              {showCombinedRules ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  Hide Combined
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-1" />
                  View Combined XML
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Combined Rules View */}
          {showCombinedRules && combinedRules && (
            <div className="mb-4 p-4 bg-divine-black rounded-lg border border-divine-gold/30">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className="text-divine-gold font-medium">{combinedRules.name}</span>
                  <span className="text-gray-400 text-sm ml-2">
                    (~{combinedRules.token_estimate} tokens)
                  </span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copyToClipboard(combinedRules.content)}
                  className="text-gray-400 hover:text-divine-gold"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-gray-400 mb-3">{combinedRules.description}</p>
              <pre className="text-xs text-gray-300 whitespace-pre-wrap overflow-auto max-h-96 bg-black/50 p-3 rounded">
                {combinedRules.content}
              </pre>
            </div>
          )}

          {/* Individual Blocks List */}
          <div className="space-y-2">
            {coreBlocks.map((block) => (
              <div key={block.silo_id}>
                <div 
                  className="flex items-center justify-between p-3 bg-divine-black/50 rounded-lg border border-divine-gold/10 hover:border-divine-gold/30 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <Badge variant={block.has_db_revision ? "default" : "outline"} className={block.has_db_revision ? "bg-blue-600" : ""}>
                      {block.has_db_revision ? <Database className="h-3 w-3 mr-1" /> : <File className="h-3 w-3 mr-1" />}
                      {block.has_db_revision ? "DB" : "File"}
                    </Badge>
                    <div>
                      <p className="text-white text-sm">
                        <span className="font-medium">{block.name}</span>
                        <span className="text-gray-400 text-xs ml-2">({block.silo_id})</span>
                      </p>
                      <p className="text-xs text-gray-400">{block.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      ~{Math.round(block.char_count / 4)} tokens
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => viewBlockContent(block.silo_id)}
                      className="text-gray-400 hover:text-divine-gold"
                      data-testid={`view-block-${block.silo_id}`}
                    >
                      {expandedBlock === block.silo_id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                    {blockDetails[block.silo_id] && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(blockDetails[block.silo_id].content)}
                        className="text-gray-400 hover:text-divine-gold"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
                {/* Expanded Content */}
                {expandedBlock === block.silo_id && blockDetails[block.silo_id] && (
                  <div className="mt-2 p-4 bg-divine-black rounded-lg border border-divine-gold/10">
                    <div className="flex items-center gap-2 mb-2 text-xs text-gray-400">
                      <span>Source: {blockDetails[block.silo_id].source}</span>
                      <span>•</span>
                      <span>File: {blockDetails[block.silo_id].file_path}</span>
                      <span>•</span>
                      <span>~{blockDetails[block.silo_id].token_estimate} tokens</span>
                    </div>
                    <pre className="text-xs text-gray-300 whitespace-pre-wrap overflow-auto max-h-96">
                      {blockDetails[block.silo_id].content}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Ref Sheets Table */}
      <Card className="bg-divine-charcoal border-divine-gold/20">
        <CardHeader>
          <CardTitle className="text-divine-gold flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Ref Sheets
          </CardTitle>
        </CardHeader>
        <CardContent>
          {artifacts.ref_sheets.length === 0 ? (
            <p className="text-gray-400 text-center py-4">No ref sheets generated yet</p>
          ) : (
            <div className="space-y-2">
              {artifacts.ref_sheets.map((sheet) => (
                <div key={`ref_${sheet.entity_id}`}>
                  <div 
                    className="flex items-center justify-between p-3 bg-divine-black/50 rounded-lg border border-divine-gold/10 hover:border-divine-gold/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <Badge variant={sheet.entity_type === "user" ? "secondary" : "outline"}>
                        {sheet.entity_type}
                      </Badge>
                      <div>
                        <p className="text-white text-sm">
                          <span className="font-medium">{sheet.name || "Unknown"}</span>
                          <span className="text-gray-400"> - Ref Sheet - </span>
                          <span className="font-mono text-xs text-gray-500">{sheet.entity_id}</span>
                        </p>
                        <p className="text-xs text-gray-400">v{sheet.version} • {formatDate(sheet.generated_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {sheet.stale ? (
                        <Badge variant="destructive" className="bg-yellow-600">Stale</Badge>
                      ) : (
                        <Badge variant="outline" className="border-green-500 text-green-500">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Fresh
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => viewRefSheet(sheet.entity_type, sheet.entity_id)}
                        className="text-gray-400 hover:text-divine-gold"
                        data-testid={`view-refsheet-${sheet.entity_id}`}
                      >
                        {expandedArtifact === `ref_${sheet.entity_id}` ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => regenerateRefSheet(sheet.entity_type, sheet.entity_id)}
                        disabled={regenerating[`ref_${sheet.entity_id}`]}
                        className="text-gray-400 hover:text-divine-gold"
                        data-testid={`regen-refsheet-${sheet.entity_id}`}
                      >
                        <RefreshCw className={`h-4 w-4 ${regenerating[`ref_${sheet.entity_id}`] ? 'animate-spin' : ''}`} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteArtifacts(sheet.entity_type, sheet.entity_id)}
                        className="text-red-400 hover:text-red-300"
                        data-testid={`delete-artifacts-${sheet.entity_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {/* Expanded Details */}
                  {expandedArtifact === `ref_${sheet.entity_id}` && artifactDetails[`ref_${sheet.entity_id}`] && (
                    <div className="mt-2 p-4 bg-divine-black rounded-lg border border-divine-gold/10">
                      <pre className="text-xs text-gray-300 whitespace-pre-wrap overflow-auto max-h-96">
                        {artifactDetails[`ref_${sheet.entity_id}`].ref_sheet?.rendered_md || 
                         JSON.stringify(artifactDetails[`ref_${sheet.entity_id}`], null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Style Cards Table */}
      <Card className="bg-divine-charcoal border-divine-gold/20">
        <CardHeader>
          <CardTitle className="text-divine-gold flex items-center gap-2">
            <User className="h-5 w-5" />
            Style Cards
          </CardTitle>
        </CardHeader>
        <CardContent>
          {artifacts.style_cards.length === 0 ? (
            <p className="text-gray-400 text-center py-4">No style cards generated yet</p>
          ) : (
            <div className="space-y-2">
              {artifacts.style_cards.map((card) => (
                <div key={`style_${card.entity_id}`}>
                  <div 
                    className="flex items-center justify-between p-3 bg-divine-black/50 rounded-lg border border-divine-gold/10 hover:border-divine-gold/30 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <Badge variant={card.entity_type === "user" ? "secondary" : "outline"}>
                        {card.entity_type}
                      </Badge>
                      <div>
                        <p className="text-white text-sm">
                          <span className="font-medium">{card.name || "Unknown"}</span>
                          <span className="text-gray-400"> - Style Card - </span>
                          <span className="font-mono text-xs text-gray-500">{card.entity_id}</span>
                        </p>
                        <p className="text-xs text-gray-400">v{card.version} • {formatDate(card.generated_at)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {card.stale ? (
                        <Badge variant="destructive" className="bg-yellow-600">Stale</Badge>
                      ) : (
                        <Badge variant="outline" className="border-green-500 text-green-500">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Fresh
                        </Badge>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => viewStyleCard(card.entity_id)}
                        className="text-gray-400 hover:text-divine-gold"
                        data-testid={`view-stylecard-${card.entity_id}`}
                      >
                        {expandedArtifact === `style_${card.entity_id}` ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => regenerateStyleCard(card.entity_id)}
                        disabled={regenerating[`style_${card.entity_id}`]}
                        className="text-gray-400 hover:text-divine-gold"
                        data-testid={`regen-stylecard-${card.entity_id}`}
                      >
                        <RefreshCw className={`h-4 w-4 ${regenerating[`style_${card.entity_id}`] ? 'animate-spin' : ''}`} />
                      </Button>
                    </div>
                  </div>
                  {/* Expanded Details */}
                  {expandedArtifact === `style_${card.entity_id}` && artifactDetails[`style_${card.entity_id}`] && (
                    <div className="mt-2 p-4 bg-divine-black rounded-lg border border-divine-gold/10">
                      <pre className="text-xs text-gray-300 whitespace-pre-wrap overflow-auto max-h-96">
                        {artifactDetails[`style_${card.entity_id}`].style_card?.rendered_md || 
                         JSON.stringify(artifactDetails[`style_${card.entity_id}`], null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <Button
          onClick={fetchArtifacts}
          variant="outline"
          className="border-divine-gold/50 text-divine-gold hover:bg-divine-gold/10"
          data-testid="refresh-artifacts"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>
    </div>
  );
}
